import { useState, useMemo } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useTenant } from '@/contexts/TenantContext'
import { isNavItemVisible, getBrandShortName } from '@/lib/tenant'
import { SportToggle } from '@/components/ui/SportToggle'
import { MobileMenu } from '@/components/MobileMenu'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import clsx from 'clsx'

const ALL_NAV_ITEMS = [
  // Home (/:sport) is the scoreboard. The filterable week view still lives at
  // /scores (reached from the homepage's "Filter & seasons" link), so it is not
  // a separate nav item.
  { label: 'Scores', path: '' },
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'RPI', path: '/rpi' },
  { label: 'Poll', path: '/coaches-poll' },
  { label: 'News', path: '/news' },
  { label: 'Bracketology', path: '/bracketology' },
  { label: 'Bubble', path: '/bubble' },
  { label: 'Transfers', path: '/transfers' },
  { label: 'Recruiting', path: '/recruiting' },
  { label: 'Highlights', path: '/highlights' },
]

export default function Layout() {
  const { sport, toggleSport } = useSport()
  const tenant = useTenant()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const brandShort = getBrandShortName(tenant.brandName)

  const NAV_ITEMS = useMemo(
    () => ALL_NAV_ITEMS.filter(item => isNavItemVisible(item.label, tenant.sportCategory)),
    [tenant.sportCategory],
  )

  return (
    <div className="min-h-screen flex flex-col bg-pitch-950">
      <header className="sticky top-0 z-30 bg-pitch-950/95 backdrop-blur-md border-b border-pitch-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-400 hover:text-white"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <Link to={`/${sport}`} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center font-black text-white text-sm">
                D1
              </div>
              <span className="text-lg font-bold text-white hidden sm:block">{brandShort}</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const isToolkit = item.path === '/recruiting-toolkit'
                const href = isToolkit ? item.path : `/${sport}${item.path}`
                const isActive = isToolkit
                  ? location.pathname === href
                  : item.path === ''
                    ? location.pathname === `/${sport}`
                    : location.pathname.startsWith(`/${sport}${item.path}`)
                return (
                  <Link
                    key={item.label}
                    to={href}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      isActive ? 'bg-accent text-white' : 'text-slate-300 hover:bg-pitch-800 hover:text-white',
                    )}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>

            <SportToggle sport={sport} onToggle={toggleSport} />
          </div>
        </div>
      </header>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sport={sport}
        onToggleSport={toggleSport}
        navItems={NAV_ITEMS}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      <footer className="border-t border-pitch-700/50 bg-pitch-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-accent/20 flex items-center justify-center text-accent text-xs font-black">D1</div>
              <span className="text-sm text-slate-500">{brandShort}</span>
            </div>
            <div className="flex flex-col sm:items-end gap-1">
              <p className="text-xs text-slate-600">
                NCAA Division I RPI, Scores &amp; Rankings. Not affiliated with the NCAA.
              </p>
              <p className="text-xs text-slate-600">
                Powered by{' '}
                <a
                  href="https://firewoodcamps.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-slate-400 hover:text-accent transition-colors"
                >
                  Firewood
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
